import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import z from "zod";
import { Button } from "@/components/base/buttons/button";
import { DeleteRecordTypeModal } from "@/components/Organisms/DeleteRecordTypeModal/DeleteRecordTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import { authQueryOptions } from "@/services/auth/auth.options";
import { organizationQueryOptions } from "@/services/organization/organization.options";
import {
	useDisableRecordType,
	useDisableRecordTypeSubOrganization,
	useDuplicateRecordType,
	useEnableRecordType,
	useEnableRecordTypeSubOrganization,
	useUpdateRecordType,
} from "@/services/record/record.mutations";
import { recordQueryOptions } from "@/services/record/record.options";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import {
	canShowAccessTab,
	canShowActivityTab,
	getRecordTypeDisplayName,
} from "@/utils/record-type-detail/record-type-detail.utils";
import {
	RecordTypeDrawerContext,
	type RecordTypeDrawerContextValue,
} from "@/utils/record-type-detail/record-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z
		.enum(["drawer", "modal", "full"])
		.optional()
		.default("drawer"),
});

type RecordTypeTab = "details" | "access" | "schema" | "rules" | "activity";

function getCurrentTab(pathname: string): RecordTypeTab {
	const segment = pathname.split("/").filter(Boolean).pop();
	if (
		segment === "details" ||
		segment === "access" ||
		segment === "schema" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

const RECORD_TYPE_DRAWER_TAB_ROUTES = {
	details:
		"/org-admin/$orgId/configuration/recordTypes/$recordTypeId/details" as const,
	access:
		"/org-admin/$orgId/configuration/recordTypes/$recordTypeId/access" as const,
	schema:
		"/org-admin/$orgId/configuration/recordTypes/$recordTypeId/schema" as const,
	rules:
		"/org-admin/$orgId/configuration/recordTypes/$recordTypeId/rules" as const,
	activity:
		"/org-admin/$orgId/configuration/recordTypes/$recordTypeId/activity" as const,
};

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/recordTypes/$recordTypeId",
)({
	component: OrgRecordTypeIdLayout,
	params: {
		parse: (raw) => {
			const p = raw as { orgId: string; recordTypeId: string };
			return {
				orgId: z.string().parse(p.orgId),
				recordTypeId: z.string().parse(p.recordTypeId),
			};
		},
	},
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params, search }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/recordTypes/${params.recordTypeId}`,
		);
		if (exactRoot) {
			const parsed = SearchSchema.safeParse(search);
			const s = parsed.success ? parsed.data : SearchSchema.parse({});
			throw redirect({
				to: "/org-admin/$orgId/configuration/recordTypes/$recordTypeId/details",
				params: { orgId: params.orgId, recordTypeId: params.recordTypeId },
				search: {
					mode: s.mode,
					presentation: s.presentation,
				},
			});
		}
	},
});

function OrgRecordTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/recordTypes/$recordTypeId",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/recordTypes/$recordTypeId",
	});

	const organizationIdForApi = params.orgId;
	const isFullRoute = /\/configuration\/recordTypes\/[^/]+\/full/.test(
		pathname,
	);
	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const detailQuery = useQuery(
		recordQueryOptions.recordTypeDetail(params.recordTypeId, {
			organizationId: organizationIdForApi,
		}),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteRecordType",
			entityType: "RecordType",
			entityId: params.recordTypeId,
		}),
	});

	const recordsCountQuery = useQuery({
		...recordQueryOptions.recordsCount({
			RecordTypeIds: [params.recordTypeId],
			OrganizationId: organizationIdForApi,
		}),
		enabled: !!canDeleteQuery.data,
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditRecordType",
			entityType: "RecordType",
			entityId: params.recordTypeId,
		}),
	});
	const accessEdit = useTypeAccessEditAllowed(
		"RecordType",
		params.recordTypeId,
	);
	const isPlatformAdminQuery = useQuery({
		...authQueryOptions.checkAction({ name: "ViewAllOrganizations" }),
	});
	const subOrgCountQuery = useQuery(
		organizationQueryOptions.subOrganizations.count(organizationIdForApi, {}),
	);
	const hasSubOrganizations = (subOrgCountQuery.data ?? 0) > 0;

	const updateMutation = useUpdateRecordType();
	const duplicateMutation = useDuplicateRecordType();
	const { duplicate: duplicateRecordType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (recordTypeId) => ({
				recordTypeId,
				organizationId: organizationIdForApi,
			}),
			onDuplicated: (recordTypeId) => {
				void navigate({
					to: "/org-admin/$orgId/configuration/recordTypes/$recordTypeId/details",
					params: { orgId: organizationIdForApi, recordTypeId },
					search: { mode: "view", presentation: "drawer" },
				});
			},
			errorMessage: t("admin.recordTypes.modal.createError"),
		});
	const enableMutation = useEnableRecordType();
	const disableMutation = useDisableRecordType();
	const enableSubOrgMutation = useEnableRecordTypeSubOrganization();
	const disableSubOrgMutation = useDisableRecordTypeSubOrganization();
	const isSavingAny =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending ||
		enableSubOrgMutation.isPending ||
		disableSubOrgMutation.isPending;

	const recordType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		recordType?.id,
		recordType?.locationRequired,
	);

	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");
	// Access draft ΓÇö null = enabled (whole-org / no sub-orgs), [] = disabled, string[] = selected sub-org IDs
	const [draftSubOrgIds, setDraftSubOrgIds] = useState<string[] | null>(null);
	const syncedRecordTypeIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!recordType?.id) return;

		const idChanged = syncedRecordTypeIdRef.current !== recordType.id;
		syncedRecordTypeIdRef.current = recordType.id;

		const isAccessEdit =
			(search.mode ?? "view") === "edit" &&
			getCurrentTab(pathname) === "access";
		const isDetailsEdit = (search.mode ?? "view") === "edit";
		const name = getRecordTypeDisplayName(recordType);
		const description = recordType.description ?? "";
		const icon = recordType.icon ?? "";
		if (idChanged || !isDetailsEdit || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (idChanged || !isDetailsEdit || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (idChanged || !isDetailsEdit || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
		if (isAccessEdit && !idChanged) return;
		const subOrgIds = (recordType.recordTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		setDraftSubOrgIds(
			subOrgIds.length > 0
				? subOrgIds
				: recordType.enabled === false
					? []
					: null,
		);
	}, [recordType, pathname, search.mode]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/org-admin/$orgId/configuration/recordTypes",
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || recordType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && recordType !== null;

	const loadedRecordType = recordType ?? undefined;
	const displayRecordType =
		loadedRecordType && isDetailReady
			? {
					...loadedRecordType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon || loadedRecordType.icon,
				}
			: null;

	const canEdit = !!loadedRecordType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedRecordType &&
		isDetailReady &&
		canShowActivityTab("organization", loadedRecordType);
	const showAccess = canShowAccessTab("organization");
	const currentTab = getCurrentTab(pathname);
	const isEditing =
		search.mode === "edit" &&
		(currentTab === "access" ? accessEdit.allowed : canEdit);

	useEffect(() => {
		if (isFullRoute || pathname.includes("/form")) return;
		if ((search.mode ?? "view") !== "edit") return;
		if (currentTab === "access") {
			if (accessEdit.isPending || accessEdit.allowed) return;
		} else if (!isDetailReady || canEditQuery.isPending || canEdit) {
			return;
		}
		void navigate({
			to: RECORD_TYPE_DRAWER_TAB_ROUTES[currentTab],
			params: {
				orgId: params.orgId,
				recordTypeId: params.recordTypeId,
			},
			search: { mode: "view", presentation: search.presentation },
		});
	}, [
		accessEdit.allowed,
		accessEdit.isPending,
		canEdit,
		canEditQuery.isPending,
		currentTab,
		isDetailReady,
		isFullRoute,
		navigate,
		params.orgId,
		params.recordTypeId,
		pathname,
		search.mode,
		search.presentation,
	]);

	const isValid = draftName.trim().length > 0;

	const tabs: { id: RecordTypeTab; label: string }[] =
		loadedRecordType && isDetailReady
			? [
					{ id: "details", label: t("admin.recordTypes.drawer.tabs.details") },
					...(showAccess
						? [
								{
									id: "access" as const,
									label: t("admin.recordTypes.drawer.tabs.access"),
								},
							]
						: []),
					{ id: "schema", label: t("admin.recordTypes.drawer.tabs.form") },
					{ id: "rules", label: t("admin.recordTypes.drawer.tabs.rules") },
					...(showActivity
						? [
								{
									id: "activity" as const,
									label: t("admin.recordTypes.drawer.tabs.activity"),
								},
							]
						: []),
				]
			: [];

	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ id: currentTabInfo.id, label: `Edit ${currentTabInfo.label}` }]
		: [];

	const canDelete =
		!!loadedRecordType &&
		isDetailReady &&
		!!canDeleteQuery.data &&
		!isEditing &&
		recordsCountQuery.data === 0;
	const canDuplicate = isDetailReady && !isEditing;

	const saveDraftFn = async (): Promise<void> => {
		if (!loadedRecordType || !isValid || isSavingAny) return;
		if (currentTab === "access" ? !accessEdit.allowed : !canEdit) return;
		try {
			if (currentTab === "access") {
				if (hasSubOrganizations) {
					const serverIds = (loadedRecordType.recordTypeSubOrganizations ?? [])
						.map((s) => s.id)
						.filter((id): id is string => id !== null);
					const nextIds = draftSubOrgIds ?? [];
					const toEnable = nextIds.filter((id) => !serverIds.includes(id));
					const toDisable = serverIds.filter((id) => !nextIds.includes(id));
					await Promise.all([
						...toEnable.map((subOrgId) =>
							enableSubOrgMutation.mutateAsync({
								typeId: loadedRecordType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
						...toDisable.map((subOrgId) =>
							disableSubOrgMutation.mutateAsync({
								typeId: loadedRecordType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
					]);
				} else {
					if (draftSubOrgIds === null) {
						await enableMutation.mutateAsync({
							id: loadedRecordType.id,
							organizationId: organizationIdForApi,
						});
					} else {
						await disableMutation.mutateAsync({
							id: loadedRecordType.id,
							organizationId: organizationIdForApi,
						});
					}
				}
				showSuccessToast({
					message: t("admin.recordTypes.drawer.access.toast.saveSuccess"),
				});
			} else {
				// Details save: preserve access sub-org list
				const currentSubOrgIds = (
					loadedRecordType.recordTypeSubOrganizations ?? []
				)
					.map((s) => s.id)
					.filter((id): id is string => id !== null);
				await updateMutation.mutateAsync({
					id: loadedRecordType.id,
					data: {
						name: draftName.trim(),
						description: draftDescription || null,
						icon: draftIcon || null,
						subOrganizationIds:
							currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
						locationRequired,
					},
					organizationId: organizationIdForApi,
				});
				setEditedName(draftName.trim());
				setEditedDescription(draftDescription);
				setEditedIcon(draftIcon);
				showSuccessToast({
					message: t("admin.recordTypes.drawer.details.toast.saveSuccess"),
				});
			}
			if (!isFullRoute) {
				void navigate({
					to: RECORD_TYPE_DRAWER_TAB_ROUTES[currentTab],
					params: {
						orgId: params.orgId,
						recordTypeId: params.recordTypeId,
					},
					search: { mode: "view", presentation: search.presentation },
				});
			}
		} catch {
			// Global mutation error handler already shows the error toast
		}
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedRecordType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		const currentSubOrgIds = (loadedRecordType.recordTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			id: loadedRecordType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired,
			},
			organizationId: organizationIdForApi,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedRecordType) return;
		const currentSubOrgIds = (loadedRecordType.recordTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			id: loadedRecordType.id,
			data: {
				name: (editedName || loadedRecordType.name || "").trim(),
				description: editedDescription || loadedRecordType.description || null,
				icon: editedIcon || loadedRecordType.icon || null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired: next,
			},
			organizationId: organizationIdForApi,
		});
		setLocationRequired(next);
	};

	const contextValue: RecordTypeDrawerContextValue | null =
		loadedRecordType && isDetailReady && displayRecordType
			? {
					scope: "organization",
					recordType: displayRecordType,
					mode: search.mode,
					setMode: (mode) => {
						if (isFullRoute) {
							void navigate({
								to: "/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full",
								params: {
									orgId: params.orgId,
									recordTypeId: params.recordTypeId,
								},
								search: {
									tab:
										currentTab === "access"
											? "access"
											: currentTab === "schema"
												? "form"
												: currentTab === "rules"
													? "rules"
													: currentTab === "activity"
														? "activity"
														: "details",
									mode,
									presentation: search.presentation,
								},
							});
							return;
						}
						void navigate({
							to: RECORD_TYPE_DRAWER_TAB_ROUTES[currentTab],
							params: {
								orgId: params.orgId,
								recordTypeId: params.recordTypeId,
							},
							search: { mode, presentation: search.presentation },
						});
					},
					activeTab: currentTab,
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds,
					setDraftSubOrgIds,
					isSaving: isSavingAny,
					isValid,
					isDirty:
						draftName.trim() !== editedName ||
						draftDescription !== editedDescription ||
						draftIcon !== editedIcon,
					saveDraft: saveDraftFn,
					cancelDraft: () => {
						setDraftName(editedName);
						setDraftDescription(editedDescription);
						setDraftIcon(editedIcon);
						const subOrgIds = (
							loadedRecordType.recordTypeSubOrganizations ?? []
						)
							.map((s) => s.id)
							.filter((id): id is string => id !== null);
						setDraftSubOrgIds(
							subOrgIds.length > 0
								? subOrgIds
								: loadedRecordType.enabled === false
									? []
									: null,
						);
						if (isFullRoute) {
							void navigate({
								to: "/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full",
								params: {
									orgId: params.orgId,
									recordTypeId: params.recordTypeId,
								},
								search: {
									tab:
										currentTab === "access"
											? "access"
											: currentTab === "schema"
												? "form"
												: currentTab === "rules"
													? "rules"
													: currentTab === "activity"
														? "activity"
														: "details",
									mode: "view",
									presentation: search.presentation,
								},
							});
							return;
						}
						void navigate({
							to: RECORD_TYPE_DRAWER_TAB_ROUTES[currentTab],
							params: {
								orgId: params.orgId,
								recordTypeId: params.recordTypeId,
							},
							search: { mode: "view", presentation: search.presentation },
						});
					},
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayRecordType !== null
			? getRecordTypeDisplayName(displayRecordType)
			: t("admin.recordTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedRecordType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedRecordType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedRecordType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedRecordType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	const editFooter =
		isEditing && currentTab !== "access" && contextValue ? (
			<div className="flex w-full items-center justify-end gap-3">
				<Button
					color="secondary"
					size="md"
					onClick={contextValue.cancelDraft}
					isDisabled={isSavingAny}
				>
					{t("common.actions.cancel")}
				</Button>
				<Button
					color="primary"
					size="md"
					data-testid="record-type-drawer-save-btn"
					onClick={() => void contextValue.saveDraft()}
					isDisabled={!isValid || isSavingAny}
					isLoading={isSavingAny}
				>
					{t("common.actions.save")}
				</Button>
			</div>
		) : undefined;

	if (isFullRoute) {
		return contextValue ? (
			<RecordTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</RecordTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	const isOnRecordTypeSubroute = /\/configuration\/recordTypes\/[^/]+\//.test(
		pathname,
	);

	if (!isOnRecordTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="record"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: "/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full",
						params: {
							orgId: params.orgId,
							recordTypeId: params.recordTypeId,
						},
						search: {
							tab: "details",
							mode: "view",
							presentation: "full",
						},
					});
				}}
				duplicateLabel={
					canDuplicate
						? t("admin.recordTypes.table.actions.duplicate")
						: undefined
				}
				onDuplicate={
					canDuplicate
						? () => duplicateRecordType(params.recordTypeId)
						: undefined
				}
				deleteLabel={
					canDelete
						? t("admin.recordTypes.drawer.footer.deleteRecordType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.recordTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.recordTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/org-admin/${params.orgId}/configuration/recordTypes/${params.recordTypeId}/${tabId}`,
						search: {
							mode: search.mode,
							presentation: search.presentation,
						},
					})
				}
				footer={editFooter}
				data-testid="record-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<RecordTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</RecordTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteRecordTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={handleClose}
				recordTypeId={params.recordTypeId}
				recordTypeName={
					loadedRecordType ? getRecordTypeDisplayName(loadedRecordType) : ""
				}
				organizationId={
					isPlatformAdminQuery.data ? organizationIdForApi : undefined
				}
				scope="organization"
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.recordTypes.duplicate.inProgress")}
				testId="recordtype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
